/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';

import { type GrammarList } from '../conditionalSearch/grammar.type';
import { batch, grammar } from './gammar';

const tipStyle = {
    style: { marginLeft: '1em', overflow: 'hidden', transform: 'translateY(-2px)' },
    value: 'i18n.get(home.search_term_sortByPerformance_info)',
};
const performanceGrammar = {
    'performanceData.getTopBatch': [{
        key: 'IOPS',
        value: 22,
        tip: tipStyle,
    }, {
        key: '带宽',
        value: 21,
        tip: tipStyle,
    }, {
        key: '时延',
        value: 370,
        tip: tipStyle,
    }],
};
const FileSystemPerformanceGrammar = {
    'performanceData.getTopBatch': [{
        key: '服务时间',
        value: 523,
        tip: tipStyle,
    }, {
        key: '文件带宽',
        value: 511,
        tip: tipStyle,
    }, {
        key: 'OPS',
        value: 182,
        tip: tipStyle,
    }],
};
const LUNGRAMMAR = {
    'lun.getBatch': [
        {
            key: '名称',
            value: 'NAME',
        }, {
            key: '容量',
            value: 'CAPACITY',
        },
        // 微存储直通场景屏蔽已分配容量，仅支持总容量
        ...(SWITCH.IS_DIRECT_MODE ? [] : [
            {
                key: '已分配容量',
                value: 'ALLOCCAPACITY',
            },
        ]),
        {
            key: '健康状态',
            value: 'HEALTHSTATUS',
        },
        {
            key: '运行状态',
            value: 'RUNNINGSTATUS',
        },
        // 微存储屏蔽功能类型
        ...(FeatureConfig.IsHideLunTableBusinessColumnDef ? [] : [
            {
                key: '功能类型',
                value: 'functionType',
            },
        ]),
        {
            key: '映射状态',
            value: FeatureConfig.IsHideLunTableBusinessColumnDef ? 'EXPOSEDTOINITIATOR' : 'mapped',
        },
        {
            key: '本端WWN',
            value: 'WWN',
        },
    ],
    // 微存储屏蔽性能监控
    ...(FeatureConfig.IsHidePms ? {} : performanceGrammar),
};
const LUNGROUPGRAMMAR = {
    'lunGroup.getBatch': [
        {
            key: '名称',
            value: 'NAME',
        },
        {
            key: '映射状态',
            value: 'ISADD2MAPPINGVIEW',
        },
    ],
    // 微存储屏蔽性能监控
    ...(FeatureConfig.IsHidePms ? {} : performanceGrammar),
};
const HOST = {
    'host.getBatch': [
        {
            key: '名称',
            value: 'NAME',
        }, {
            key: '健康状态',
            value: 'HEALTHSTATUS',
        },
        {
            key: '操作系统',
            value: 'OPERATIONSYSTEM',
        },
        {
            key: 'IP地址',
            value: 'IP',
        },
    ],
    // 微存储屏蔽性能监控
    ...(FeatureConfig.IsHidePms ? {} : performanceGrammar),
};
const HOSTGROUP = {
    'hostGroup.getBatch': [
        {
            key: '名称',
            value: 'NAME',
        },
    ],
    // 微存储屏蔽性能监控
    ...(FeatureConfig.IsHidePms ? {} : performanceGrammar),
};
const PORTGROUP = {
    'portGroup.getBatch': [
        {
            key: '名称',
            value: 'NAME',
        },
        {
            key: '映射状态',
            value: 'isAddToMappingView',
        },
    ],
};
const LOGICPORT = {
    'lif.getBatch': [
        {
            key: '名称',
            value: 'NAME',
        },
        {
            key: '运行状态',
            value: 'RUNNINGSTATUS',
        },
        {
            key: '激活状态',
            value: 'OPERATIONALSTATUS',
        },
        {
            key: '数据协议',
            value: 'SUPPORTPROTOCOL',
        },
        {
            key: 'IPv4地址',
            value: 'IPV4ADDR',
        }, {
            key: 'IPv4子网掩码',
            value: 'IPV4MASK',
        }, {
            key: 'IPv4网关',
            value: 'IPV4GATEWAY',
        }, {
            key: 'IPv6地址',
            value: 'IPV6ADDR',
        }, {
            key: 'IPv6子网掩码',
            value: 'IPV6MASK',
        }, {
            key: 'IPv6网关',
            value: 'IPV6GATEWAY',
        }, {
            key: '主端口',
            value: 'HOMEPORTNAME',
        },
        ...(FeatureConfig.IsHideLogicPortCurrentPort ? [] : [
            {
                key: '当前端口',
                value: 'CURRENTPORTNAME',
            },
        ]),
    ],
    // 微存储屏蔽性能监控
    ...(FeatureConfig.IsHidePms ? {} : performanceGrammar),
};
const TENANT = {
    'vstore.getBatch': [
        {
            key: '名称',
            value: 'NAME',
        }, {
            key: '运行状态',
            value: 'RUNNINGSTATUS',
        },
    ],
};
const LUNSNAPSHOT = {
    'snapshot.getSnapshot': [
        {
            key: '名称',
            value: 'NAME',
        }, {
            key: '健康状态',
            value: 'HEALTHSTATUS',
        }, {
            key: '运行状态',
            value: 'RUNNINGSTATUS',
        }, {
            key: '源LUN',
            value: 'SOURCELUNNAME',
        }, {
            key: '父对象',
            value: 'PARENTNAME',
        }, {
            key: '激活时间点',
            value: 'TIMESTAMP',
        },
        {
            key: '本端WWN',
            value: 'WWN',
        },
    ],
};
const FILESYSTEM = {
    'fileSystem.getBatch': [
        {
            key: '名称',
            value: 'NAME',
        },
        {
            key: '健康状态',
            value: 'HEALTHSTATUS',
        }, {
            key: '运行状态',
            value: 'RUNNINGSTATUS',
        },
    ],
    ...FileSystemPerformanceGrammar,
};
const keyMap = {
    LUNs: LUNGRAMMAR,
    LUNGroups: LUNGROUPGRAMMAR,
    Namespaces: LUNGRAMMAR,
    NamespaceGroups: LUNGROUPGRAMMAR,
    Hosts: HOST,
    HostGroups: HOSTGROUP,
    PortGroups: PORTGROUP,
    LogicalPorts: LOGICPORT,
    vStores: TENANT,
    LUNSnapshots: LUNSNAPSHOT,
    FileSystems: FILESYSTEM,
};
const grammarList: GrammarList = batch(grammar, keyMap);
export default grammarList;
